import { fetchProcessingDocumentCount } from "@/stores/documents/repository";
import { countIssuesCreatedSince } from "@/stores/pm/repository";

/**
 * Proactive chat nudges — small cards in the bottom-right lane that appear when
 * something REAL happens. Every nudge here is wired to an actual event source;
 * nothing is simulated:
 *
 * - `processing_done`  → documents.state count for the building (scoped to
 *                        documents created since the batch started uploading),
 *                        polled while the batch works through the pipeline
 * - `new_issues`       → pm_issues created in the last 24h
 *
 * There is deliberately NO "uploading" nudge: `UploadQueueProgress` owns live
 * progress and lets the user opt in to the processing-complete nudge.
 *
 * Guardrails (all session-scoped, held in sessionStorage — never the DB):
 * - at most one nudge visible at a time; one arriving while another is on
 *   screen (or while the open panel covers the lane) queues and shows once
 *   the slot frees — never silently replacing a nudge the user hasn't seen
 * - one per session per type, counted when actually displayed
 * - a dismissed type stays quiet for the rest of the session
 *
 * The state machine lives outside React on purpose: "one nudge at a time" is a
 * single app-wide fact, and keeping it here lets `useChatNudges` subscribe with
 * `useSyncExternalStore` instead of pushing state around inside effects.
 */
export type ChatNudgeType = "processing_done" | "new_issues";

export type ChatNudge =
  | { type: "processing_done"; count: number }
  | { type: "new_issues"; count: number };

/** How far back "new issues" looks. */
const NEW_ISSUES_WINDOW_MS = 24 * 60 * 60 * 1000;
/** Poll cadence while an upload batch is still processing. */
const PROCESSING_POLL_MS = 5000;
/** Give up watching a batch after this long — better silent than wrong. */
const PROCESSING_WATCH_TIMEOUT_MS = 15 * 60 * 1000;

const SHOWN_KEY = "franklin.chat-nudge.shown";
const DISMISSED_KEY = "franklin.chat-nudge.dismissed";

function readTypes(key: string): Set<string> {
  try {
    const raw = sessionStorage.getItem(key);
    return new Set<string>(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set<string>();
  }
}

function markType(key: string, type: ChatNudgeType): void {
  try {
    const set = readTypes(key);
    set.add(type);
    sessionStorage.setItem(key, JSON.stringify(Array.from(set)));
  } catch {
    // sessionStorage unavailable (private mode / SSR) — the caps degrade to
    // "per mount", which is still never noisier than one nudge at a time.
  }
}

/** Frequency cap + dismissed-type check. Exported for the guardrail tests. */
export function canShowNudge(type: ChatNudgeType): boolean {
  return !readTypes(SHOWN_KEY).has(type) && !readTypes(DISMISSED_KEY).has(type);
}

let current: ChatNudge | null = null;
// Nudges that arrived while another was on screen — shown once the slot frees.
let pending: ChatNudge[] = [];
// Whether the collapsed pill's lane (the nudge's display surface) is on
// screen. While the open panel covers it, nudges queue instead of displaying,
// so a once-per-session slot is only spent on a render the user can see.
let laneVisible = true;
const listeners = new Set<() => void>();

function set(next: ChatNudge | null): void {
  current = next;
  for (const listener of listeners) listener();
}

function display(next: ChatNudge | null): void {
  if (next) markType(SHOWN_KEY, next.type);
  set(next);
}

/**
 * Show `next` if the slot is free and nothing arrived before it, else queue
 * it — replacing a visible nudge would burn its once-per-session cap without
 * the user ever seeing or dismissing it, and jumping an earlier-queued nudge
 * would break FIFO order. A duplicate of the visible type refreshes in place
 * (checked before the SHOWN cap, which the visible nudge has already burned);
 * a queued duplicate is replaced so its data stays fresh.
 */
function show(next: ChatNudge): void {
  if (current?.type === next.type) {
    // Refresh in place — the slot was already counted when it displayed.
    set(next);
    return;
  }
  if (!canShowNudge(next.type)) return;
  pending = [...pending.filter((p) => p.type !== next.type), next];
  if (!current && laneVisible) advance();
}

/** Hand the slot to the next queued nudge (skipping types silenced meanwhile). */
function advance(): void {
  let next: ChatNudge | null = null;
  // A covered lane promotes nothing: the queue holds until it is visible again.
  while (laneVisible && pending.length > 0) {
    const candidate = pending.shift() as ChatNudge;
    if (canShowNudge(candidate.type)) {
      next = candidate;
      break;
    }
  }
  display(next);
}

/**
 * Tell the store whether the collapsed pill's lane is on screen. The open
 * panel covers the lane, so a nudge displayed under it would spend its
 * once-per-session slot without ever being seen — while hidden, arrivals
 * queue, and the queue drains when the lane comes back.
 */
export function setNudgeLaneVisible(visible: boolean): void {
  laneVisible = visible;
  if (visible && !current) advance();
}

export function getNudge(): ChatNudge | null {
  return current;
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    // Nothing is rendering nudges any more: drop the transient state (the
    // session caps stay in sessionStorage) and stop polling.
    if (listeners.size === 0) reset();
  };
}

/** Dismiss the visible nudge — its type stays quiet for the rest of the session. */
export function dismiss(): void {
  if (current) markType(DISMISSED_KEY, current.type);
  advance();
}

/** Hide the visible nudge without silencing its type (e.g. after acting on it). */
export function clear(): void {
  advance();
}

// --- Uploading ---------------------------------------------------------------

let wasUploading = false;
// When the current batch started uploading — document rows are created after
// each file uploads, so this bounds the batch's `created_at` from below.
let uploadStartedAt: string | null = null;
// Which building the batch was uploaded to, stamped at upload start. Uploads
// target the page they were launched from, so the panel's live `buildingId`
// at completion time can differ after a mid-upload building switch — the
// watch must use (and be gated on) this one, never the live param.
let uploadBuildingId: string | null = null;
// Completed uploads attributable to EARLIER batches: `done` as of the last
// idle snapshot (queue empty or fully complete). The queue accumulates until
// the user clears it, so a later batch's `done` includes every earlier batch —
// only the delta above this baseline belongs to the finishing batch.
let completedBaseline = 0;

interface FinishedUploadBatch {
  buildingId: string;
  count: number;
  startedAt: string | null;
}

/** Most recent completed upload, retained so the status bubble can opt in. */
let latestFinishedBatch: FinishedUploadBatch | null = null;

export interface UploadSnapshot {
  buildingId: string;
  total: number;
  done: number;
  isComplete: boolean;
}

/**
 * A watch belongs to the building it was earned in, and its nudge acts on the
 * ACTIVE building's chat ("Summarise" sends a prompt there) — so a watch
 * surviving a building switch would announce building A's documents inside
 * building B. Drop it silently, per the guardrails above.
 *
 * ponytail: single watch slot; per-building watches only if cross-building
 * nudges ever get building-scoped rendering.
 */
function dropWatchForOtherBuilding(buildingId: string): void {
  if (watchedBuildingId !== null && watchedBuildingId !== buildingId) stopWatching();
}

/** Note a batch still uploading, stamping its start. True while one is in flight. */
function trackUploadInFlight(buildingId: string, total: number, isComplete: boolean): boolean {
  if (total <= 0 || isComplete) return false;
  if (!wasUploading) {
    uploadStartedAt = new Date().toISOString();
    uploadBuildingId = buildingId;
  }
  wasUploading = true;
  return true;
}

/**
 * Clear the in-flight batch and hand back when it started uploading and which
 * building it targeted — or `null` if nothing was in flight, i.e. this
 * snapshot is not a batch finishing.
 */
function takeFinishedBatch(): { startedAt: string | null; buildingId: string | null } | null {
  if (!wasUploading) return null;
  const startedAt = uploadStartedAt;
  const buildingId = uploadBuildingId;
  wasUploading = false;
  uploadStartedAt = null;
  uploadBuildingId = null;
  return { startedAt, buildingId };
}

/**
 * Feed the live upload-queue numbers in. Progress itself is
 * `UploadQueueProgress`'s job. A completed batch is retained here so its
 * "Tell me when it's done" action can start a correctly scoped watch.
 */
export function syncUpload({ buildingId, total, done, isComplete }: UploadSnapshot): void {
  dropWatchForOtherBuilding(buildingId);
  if (trackUploadInFlight(buildingId, total, isComplete)) return;

  // Idle snapshot: `done` is cumulative across the uncleared queue, so only
  // its rise above the pre-batch baseline earns the nudge (and sizes the
  // summarise prompt); everything seen now becomes the next batch's baseline.
  const finished = takeFinishedBatch();
  const batchDone = done - completedBaseline;
  completedBaseline = done;
  if (!finished || finished.buildingId !== buildingId) return;
  if (batchDone > 0) {
    latestFinishedBatch = {
      buildingId,
      count: batchDone,
      startedAt: finished.startedAt,
    };
  }
}

// --- Processing done ---------------------------------------------------------

let watchTimer: ReturnType<typeof setInterval> | null = null;
/** Which building the running watch was started for — see `syncUpload`. */
let watchedBuildingId: string | null = null;
// Bumped on every stop/start. `stopWatching` only clears the interval — a
// poll already awaiting its fetch resolves afterwards regardless, so each
// poll captures the generation it belongs to and bails on a mismatch.
let watchGeneration = 0;

function stopWatching(): void {
  watchGeneration += 1;
  if (watchTimer !== null) clearInterval(watchTimer);
  watchTimer = null;
  watchedBuildingId = null;
}

/**
 * Watch the building's processing count after an upload batch finished, and
 * announce completion only once the pipeline was actually seen working — a
 * count that was zero all along means the documents never reached us.
 *
 * The count is scoped to documents created since the batch started uploading,
 * so an older stuck/reprocessing document can't hold the count above zero
 * forever, and an unrelated pre-existing document finishing can't satisfy
 * `sawProcessing` and fire a nudge this batch didn't earn. Ceiling: a
 * concurrent upload to the same building in the same window is still counted,
 * and a client clock running well ahead of the DB excludes the batch itself —
 * which fails silent (no nudge), per the guardrails above.
 */
function watchProcessing(
  buildingId: string,
  size: number,
  createdSince: string | null,
  alreadySawProcessing = false
): void {
  stopWatching();
  if (!buildingId) return;
  watchedBuildingId = buildingId;
  const generation = watchGeneration;

  const startedAt = Date.now();
  let sawProcessing = alreadySawProcessing;

  /** `null` when the count could not be read — the watch gives up on it. */
  const readProcessingCount = async (): Promise<number | null> => {
    try {
      return await fetchProcessingDocumentCount(buildingId, createdSince ?? undefined);
    } catch {
      return null;
    }
  };

  // Stopped or replaced while a fetch was in flight (building switch, reset, a
  // newer batch) — a result from an older generation no longer speaks for the
  // watch, and must not stop the one that replaced it.
  const isCurrent = () => generation === watchGeneration;

  const poll = async () => {
    const processing = await readProcessingCount();
    if (!isCurrent()) return;
    // Unreadable count, or the pipeline is taking longer than the watch window:
    // stop polling rather than claim a completion we haven't observed.
    if (processing === null || Date.now() - startedAt > PROCESSING_WATCH_TIMEOUT_MS) {
      stopWatching();
      return;
    }
    if (processing > 0) {
      sawProcessing = true;
      return;
    }
    if (!sawProcessing) return;
    stopWatching();
    show({ type: "processing_done", count: size });
  };

  watchTimer = setInterval(poll, PROCESSING_POLL_MS);
  void poll();
}

/**
 * Subscribe the active building to one real processing-complete nudge. The
 * action is shown only while the document store has already observed work in
 * progress, so that observation can safely seed the watch instead of waiting
 * for the repository poll to see the same transient state again.
 */
export function requestProcessingCompletionNudge(buildingId: string, count: number): boolean {
  if (!canRequestProcessingCompletionNudge(buildingId, count)) return false;

  const batch = matchingFinishedBatch(buildingId);
  watchProcessing(buildingId, batchCount(batch, count), batchStart(batch), true);
  return true;
}

function canRequestProcessingCompletionNudge(buildingId: string, count: number): boolean {
  return buildingId.length > 0 && count > 0 && canShowNudge("processing_done");
}

function matchingFinishedBatch(buildingId: string): FinishedUploadBatch | null {
  if (latestFinishedBatch?.buildingId !== buildingId) return null;
  return latestFinishedBatch;
}

function batchCount(batch: FinishedUploadBatch | null, fallback: number): number {
  return batch ? batch.count : fallback;
}

function batchStart(batch: FinishedUploadBatch | null): string | null {
  return batch ? batch.startedAt : null;
}

/**
 * Announce that a batch of documents finished processing and tagging. This is
 * the completion surface for background jobs (e.g. AI classification) that
 * would otherwise fire one toast per document and compete with the chat.
 */
export function showProcessingDone(count: number): void {
  if (count > 0) show({ type: "processing_done", count });
}

// --- New issues --------------------------------------------------------------

// Scopes already queried this session. Only matters for zero-result scopes
// (count returned 0, so no nudge showed and the session cap wasn't burned):
// it stops remounts and building/project switches from re-firing the same
// count query. Deliberately narrower than the SHOWN/DISMISSED caps, which are
// global per type — one nudge per session app-wide, per the header guardrails.
const checkedScopes = new Set<string>();

/**
 * One-shot check for issues opened in the last 24h on this building, narrowed
 * to the chat's active project scope when one is set — so the count matches
 * the context the user is in.
 */
export function checkNewIssues(buildingId: string, projectId?: string | null): void {
  const numericId = Number(buildingId);
  if (!buildingId || !Number.isFinite(numericId)) return;
  const projectIdNum =
    projectId && Number.isFinite(Number(projectId)) ? Number(projectId) : undefined;
  const scopeKey = `${buildingId}:${projectIdNum ?? ""}`;
  if (checkedScopes.has(scopeKey) || !canShowNudge("new_issues")) return;
  checkedScopes.add(scopeKey);

  const since = new Date(Date.now() - NEW_ISSUES_WINDOW_MS).toISOString();
  countIssuesCreatedSince(numericId, since, projectIdNum)
    .then((count) => {
      if (count > 0) show({ type: "new_issues", count });
    })
    .catch(() => {
      // A failed count is not worth surfacing — stay quiet.
    });
}

/** Drop all transient state. Session caps live in sessionStorage and survive. */
function reset(): void {
  stopWatching();
  current = null;
  pending = [];
  laneVisible = true;
  wasUploading = false;
  uploadStartedAt = null;
  uploadBuildingId = null;
  completedBaseline = 0;
  latestFinishedBatch = null;
  checkedScopes.clear();
}
